import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { ApiError, api, session } from '../lib/api.js';
import {
  Button,
  Callout,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Field,
  Input,
  Select,
  Switch,
  useToast,
} from '../ui/index.js';

/**
 * Creating a fulfilment group.
 *
 * The id is the part that matters and the part nobody thinks about: it goes
 * into every URL and every foreign key, and it cannot be changed afterwards
 * without orphaning everything that references it. So it is derived from the
 * name as a suggestion, shown plainly, and stated to be permanent — rather
 * than being hidden and generated, which is how a group ends up called
 * `esa-publications-2`.
 *
 * On success the new group is switched to immediately. Creating something and
 * then having to hunt for it is the kind of small friction that makes people
 * believe a feature is broken.
 */
export function NewGroupDialog({ trigger }: { trigger: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [idEdited, setIdEdited] = useState(false);
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [starter, setStarter] = useState(true);
  const qc = useQueryClient();
  const toast = useToast();

  /** "ESA Publications" -> "esapublications". Lowercase slug, digits allowed. */
  const slugify = (v: string): string =>
    v
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^[^a-z]+/, '')
      .slice(0, 31);

  const idOk = /^[a-z][a-z0-9_]{1,30}$/.test(id);
  const nameOk = name.trim().length >= 2;

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; name: string }>('/api/v1/tenants', {
        id,
        name: name.trim(),
        timezone,
        withStarterConfig: starter,
      }),
    onSuccess: (out) => {
      toast.success(`${out.name} created`, 'You are its administrator.');
      // Switch to it and reload, so every query refetches against the new
      // group rather than showing the old one's data under a new name.
      session.tenant = out.id;
      void qc.invalidateQueries();
      window.location.href = '/queue';
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof ApiError ? err.message : 'The group could not be created',
        err instanceof ApiError && err.fields
          ? err.fields.map((f) => `${f.key}: ${f.message}`).join(' · ')
          : undefined,
      );
    },
  });

  const reset = () => {
    setName('');
    setId('');
    setIdEdited(false);
    setStarter(true);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        title="New fulfilment group"
        description="A group is a separate tenant: its own queues, request types, fields, SLA targets and access. Nothing crosses between them."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="space-y-3">
            <Field label="Name" hint="What people will see. Can be changed later." required>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!idEdited) setId(slugify(e.target.value));
                }}
                placeholder="ESA Facilities"
                autoFocus
              />
            </Field>

            <Field
              label="Identifier"
              hint="Appears in every URL. Lowercase letters, digits and underscores. CANNOT be changed later."
              error={id.length > 0 && !idOk ? 'Must start with a letter; lowercase, digits and underscores only' : undefined}
              required
            >
              <Input
                value={id}
                onChange={(e) => {
                  setIdEdited(true);
                  setId(e.target.value);
                }}
                placeholder="esafacilities"
                className="font-mono"
              />
            </Field>

            <Field
              label="Timezone"
              hint="The desk's own working day. The SLA clock is wrong without it."
            >
              <Select
                value={timezone}
                onValueChange={setTimezone}
                options={[
                  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
                  { value: 'America/Denver', label: 'Mountain — Denver' },
                  { value: 'America/Chicago', label: 'Central — Chicago' },
                  { value: 'America/New_York', label: 'Eastern — New York' },
                ]}
                aria-label="Timezone"
              />
            </Field>

            <Switch
              checked={starter}
              onCheckedChange={setStarter}
              label="Set it up so it works immediately"
            />
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              {starter
                ? 'Adds a Triage queue, 08:00–17:00 business hours with US holidays, a 2h/2-day SLA target and four generic request types. All of it editable in Configuration afterwards.'
                : 'The group will exist with nothing in it. Until it has a default queue and a calendar it cannot route a ticket or compute an SLA target.'}
            </p>

            {!starter && (
              <Callout tone="warn" icon={<AlertTriangle />}>
                An empty group cannot take a ticket yet. Add a default queue and a business
                calendar in Configuration first.
              </Callout>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={create.isPending} disabled={!idOk || !nameOk}>
              Create group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
